/**
 * Caruselul din cardul hero DXD.
 *
 * Un slide activ la un moment dat, cu puncte și săgeți. Derularea automată se
 * oprește la hover, la focus în interior și când tab-ul nu e vizibil, și se
 * dezactivează complet dacă utilizatorul a cerut mai puțină mișcare.
 */
class DxdHeroCarousel extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #touch = null;
  #swiped = false;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.dxd-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('[data-dxd-hero-dot]'));

    if (this.slides.length <= 1) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerCancel);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onClick = (event) => {
    // Un swipe care începe peste un buton nu trebuie să îl și apese.
    if (this.#swiped) {
      this.#swiped = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const dot = event.target.closest('[data-dxd-hero-dot]');
    if (dot) {
      this.#goTo(Number(dot.dataset.dxdHeroDot));
      return;
    }
    if (event.target.closest('[data-dxd-hero-prev]')) this.#goTo(this.#index - 1);
    if (event.target.closest('[data-dxd-hero-next]')) this.#goTo(this.#index + 1);
  };

  /* ---- Swipe ----
     Pe telefon slide-urile se schimbă trăgând cu degetul oriunde pe card,
     inclusiv peste imagine. Gestul se acceptă doar dacă mișcarea e clar
     orizontală; altfel îl lăsăm în seama paginii, ca scrollul vertical să
     rămână neatins (`touch-action: pan-y` face partea de browser). */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#touch = { x: event.clientX, y: event.clientY, time: Date.now() };
  };

  #onPointerUp = (event) => {
    const start = this.#touch;
    this.#touch = null;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // Prea scurt, prea vertical sau prea lent — nu e swipe.
    if (Math.abs(dx) < 40) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    if (Date.now() - start.time > 800) return;

    this.#swiped = true;
    window.setTimeout(() => {
      this.#swiped = false;
    }, 400);

    // Circular: de la ultimul se trece la primul și invers, prin #goTo.
    this.#goTo(dx < 0 ? this.#index + 1 : this.#index - 1);
  };

  #onPointerCancel = () => {
    this.#touch = null;
  };

  #onKeydown = (event) => {
    if (!event.target.closest('.dxd-hero__controls')) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goTo(this.#index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goTo(this.#index + 1);
    }
  };

  #onVisibility = () => {
    if (document.hidden) this.#stopAutoplay();
    else this.#startAutoplay();
  };

  #pause = () => {
    this.#paused = true;
    this.#stopAutoplay();
  };

  #resume = () => {
    this.#paused = false;
    this.#startAutoplay();
  };

  #goTo(next) {
    const total = this.slides.length;
    this.#index = ((next % total) + total) % total;

    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.#index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Alt slide înseamnă alt titlu, deci alt necesar de spațiu pe mobil.
    this.dispatchEvent(new CustomEvent('dxd:hero-slide', { bubbles: true, detail: { index: this.#index } }));

    // Repornim cronometrul, ca un click manual să dea slide-ului timpul întreg.
    this.#startAutoplay();
  }

  #startAutoplay() {
    this.#stopAutoplay();

    const delay = Number(this.dataset.autoplay);
    if (!delay || this.#paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#timer = window.setInterval(() => this.#goTo(this.#index + 1), delay);
  }

  #stopAutoplay() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('dxd-hero-carousel')) {
  customElements.define('dxd-hero-carousel', DxdHeroCarousel);
}
